import React, {useMemo, useState, useEffect} from 'react'
import {Handle, Position, NodeProps, useNodes, type Node, type Edge} from 'reactflow'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import {LOGIC, NodeType} from '../../../../../../Utils/Constant/FlowConstants'
import {ConditionType, DataNode, MetaDataWithCondition} from '../../FlowTypes'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {CONDITION_TYPES} from './ConditionalSidebar'
import {useReactFlow} from 'reactflow'
import {useFlowNodeDelete} from '../hooks/useFlowNodeDelete'
import {getConditionDeleteOptions, BranchType} from '../hooks/useConditionNodeDelete'
import ConditionDeleteModal from './ConditionDeleteModal'

export interface ConditionalNodeProps extends NodeProps {
  pushToUndoStack?: (nodes: Node[], edges: Edge[]) => void
  isUndoRedoOperation?: React.MutableRefObject<boolean>
}

const ConditionalNode: React.FC<ConditionalNodeProps> = ({
  id,
  selected,
  data,
  pushToUndoStack,
  isUndoRedoOperation,
}) => {
  const {t} = useI18n()
  const {setNodes, setEdges, getEdges} = useReactFlow()
  const nodes = useNodes()
  const edges = getEdges()
  const [localError, setLocalError] = useState<string | null>(null)
  const [hovered, setHovered] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const node = useMemo(() => nodes.find((n) => n.id === id), [nodes, id])
  const {condition} =
    (node as Node<DataNode<MetaDataWithCondition>, NodeType>)?.data?.metaData || {}
  const typesWithoutValue = [
    ConditionType.SENT,
    ConditionType.FAILED,
    ConditionType.DELIVERED,
    ConditionType.ANY_MESSAGE_EXCEPT_OPTOUT,
  ]

  useEffect(() => {
    const validationError = validateConditionalNode(node?.data as DataNode<MetaDataWithCondition>)
    setLocalError(validationError)
  }, [condition, node?.data])

  useEffect(() => {
    if (localError) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node?.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  error: localError,
                },
              }
            : n
        )
      )
    } else {
      // Clear error when condition is properly set
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node?.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  error: null,
                },
              }
            : n
        )
      )
    }
  }, [localError, setNodes, node?.id])

  const needsValue = (type: string) => !typesWithoutValue.includes(type as ConditionType)

  const buildText = (label: string | undefined, type: ConditionType) => {
    if (typesWithoutValue.includes(type)) {
      if (type === ConditionType.ANY_MESSAGE_EXCEPT_OPTOUT) {
        return label
      }
      return `Status is ${label?.toLowerCase()}`
    }
    return `${label}:`
  }

  const renderLabel = (() => {
    if (!condition?.[0]?.type) {
      return 'No conditions'
    }
    const label = CONDITION_TYPES.find((c) => c.value === condition?.[0]?.type)?.label
    return buildText(label, condition?.[0]?.type as ConditionType)
  })()

  const renderValue = (() => {
    const val = condition?.[0]?.value
    if (!val || (Array.isArray(val) && val.length === 0)) {
      return 'No value'
    }
    if (Array.isArray(val)) {
      const joined = val.filter((v) => v && v.trim() !== '').join(' OR ')
      if (joined.length > 40) {
        return joined.slice(0, 40) + '...'
      }
      return joined
    }
    if (typeof val === 'string' && val.length > 40) {
      return val.slice(0, 40) + '...'
    }
    return val
  })()

  // For tooltip, always show the full joined value
  const renderValueTitle = (() => {
    const val = condition?.[0]?.value
    if (!val || (Array.isArray(val) && val.length === 0)) {
      return 'No value'
    }
    if (Array.isArray(val)) {
      return val.filter((v) => v && v.trim() !== '').join(' OR ')
    }
    return val
  })()

  const item = LOGIC[0]
  const isSelected = selected

  const error = localError || data?.error
  const hasError = !!error
  const {handleDelete, handleConditionDelete} = useFlowNodeDelete({
    nodes,
    edges,
    setNodes,
    setEdges,
    pushToUndoStack,
    isUndoRedoOperation,
  })

  const deleteOptions = getConditionDeleteOptions(nodes, edges, id)

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteModal(true)
  }

  const handleModalConfirm = (branchType: BranchType) => {
    handleConditionDelete(id, branchType)
  }

  const handleModalHide = () => {
    setShowDeleteModal(false)
  }

  return (
    <>
      <div
        className={`p-3 rounded bg-white text-center position-relative shadow-sm${
          isSelected ? ' border border-2 border-primary' : ''
        }${hasError ? ' border-danger' : ''} nopan${hovered ? ' node-hover' : ''}`}
        style={{
          width: 250,
          minHeight: 90,
          borderWidth: hasError ? '2px' : '1px',
          borderStyle: 'solid',
          borderColor: hasError
            ? 'var(--bs-danger)'
            : isSelected
            ? 'var(--bs-primary)'
            : 'var(--bs-gray-300)',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title={hasError ? `Error: ${error}` : t('FLOW.NODE.CONDITION_DESC')}
      >
        {node?.data && (node.data as any)?.deletable !== false && (
          <div className='position-absolute end-0 top-0 m-2' style={{zIndex: 10}}>
            <button className='btn btn-link p-0' title='Delete Node' onClick={handleDeleteClick}>
              <i className='bi bi-trash text-danger fs-5'></i>
            </button>
          </div>
        )}
        <Handle
          type='target'
          position={Position.Top}
          id='conditional-top'
          className={hasError ? 'bg-danger' : 'bg-success'}
        />
        <div className='d-flex flex-column gap-2'>
          <div className='d-flex align-items-center gap-2'>
            <div
              className='automation-icon-container'
              style={{
                backgroundColor: `${item.bgColor}`,
              }}
            >
              <img
                src={toAbsoluteUrl(item.svg || '')}
                alt={item.icon}
                className='automation-icon'
              />
            </div>

            <span
              className='text-truncate fw-medium'
              style={{maxWidth: '140px'}}
              title={t(`FLOW.NODE.${item.type.toUpperCase()}`)}
            >
              {t(`FLOW.NODE.${item.type.toUpperCase()}`)}
            </span>
          </div>

          <div
            className='d-grid'
            style={{
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'start',
              width: '100%',
              gap: '0.5rem',
            }}
          >
            <p
              className='mb-0 small align-self-start mt-2'
              title={renderLabel}
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              {renderLabel}
            </p>
            {needsValue(condition?.[0]?.type || '') && (
              <>
                <p
                  className='mb-0 small align-self-start mt-2 text-truncate fw-medium'
                  style={{
                    fontWeight: 600,
                    maxWidth: 120,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                  }}
                  title={renderValueTitle}
                >
                  {renderValue}
                </p>
              </>
            )}
          </div>

          {hasError && (
            <div
              className='text-danger fw-bold small mb-3 mt-2 px-2'
              style={{
                lineHeight: 1.2,
              }}
            >
              ⚠ {error}
            </div>
          )}
        </div>

        <div style={{paddingBottom: '8px'}}></div>

        <Handle
          type='source'
          position={Position.Bottom}
          id='conditional-bottom'
          className='bg-success'
        />
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <ConditionDeleteModal
          show={showDeleteModal}
          onHide={handleModalHide}
          onConfirm={handleModalConfirm}
          deleteOptions={deleteOptions as BranchType[]}
        />
      )}
    </>
  )
}

export default ConditionalNode

export const validateConditionalNode = (data: DataNode<MetaDataWithCondition>) => {
  const cond = data?.metaData?.condition?.[0]
  const typesWithValue = [
    ConditionType.REPLY_CONTAIN,
    ConditionType.REPLY_START_WITH,
    ConditionType.REPLY_END_WITH,
    ConditionType.EQUAL,
  ]
  if (cond && typesWithValue.includes(cond.type as ConditionType)) {
    if (Array.isArray(cond.value)) {
      if (cond.value.length === 0 || cond.value.every((v) => !v || v.trim() === '')) {
        return 'Value is required'
      }
    } else {
      if (!cond.value || cond.value.trim() === '') {
        return 'Condition value is required'
      }
    }
  }
  return null
}
